/**
 * Autocorrection of DSkhema sources (ТЗ 6a.5).
 *
 * Two kinds of corrections:
 * - safe ones (`safeCorrections`): typographic arrows that are not part of the language (`→`, `—>`,
 *   `⇒`, `↔`…) become the language's arrows. The meaning cannot change, so the editor applies them
 *   right while typing or pasting;
 * - suggestions (`Diagnostic.fix`): typos in keywords, a missing `{` or `}`, a node name that is
 *   probably misspelled. They may change the meaning, so the user confirms each («Исправить»).
 */

/** Replace columns [col, end) of a 1-based line with `text`. A line past the end appends. */
export interface TextFix {
  line: number;
  col: number;
  end: number;
  text: string;
}

/** A replacement in the source string: [from, to) → text. */
export interface SourceEdit {
  from: number;
  to: number;
  text: string;
}

/** Typographic arrows → the language's arrows; longest first. */
const ARROWS: [string, string][] = [
  ['<—>', '<->'],
  ['<–>', '<->'],
  ['⟷', '<->'],
  ['↔', '<->'],
  ['—>', '->'],
  ['–>', '->'],
  ['⟶', '->'],
  ['→', '->'],
  ['➔', '->'],
  ['⇒', '=>'],
  ['⟹', '=>'],
];

/** Positions of comment start in a line (`//` or `%%` after a space or at the start), outside quotes. */
function codeEnd(line: string): number {
  let q = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (c === '"') q = !q;
    if (q) continue;
    if ((c === '/' && line[i + 1] === '/') || (c === '%' && line[i + 1] === '%')) {
      if (i === 0 || /\s/.test(line[i - 1])) return i;
    }
  }
  return line.length;
}

/** Safe corrections of the whole source: arrows outside quotes and comments. */
export function safeCorrections(source: string): SourceEdit[] {
  const out: SourceEdit[] = [];
  let base = 0;
  for (const line of source.split('\n')) {
    const end = codeEnd(line);
    let q = false;
    for (let i = 0; i < end; i++) {
      if (line[i] === '"') {
        q = !q;
        continue;
      }
      if (q) continue;
      const hit = ARROWS.find(([from]) => line.startsWith(from, i));
      if (hit) {
        // keep a space around the arrow so `a→b` becomes `a -> b`
        const before = i > 0 && !/\s/.test(line[i - 1]) ? ' ' : '';
        // at the end of a line too: the next name is typed after a space
        const after = i + hit[0].length >= line.length || !/\s/.test(line[i + hit[0].length]) ? ' ' : '';
        out.push({ from: base + i, to: base + i + hit[0].length, text: before + hit[1] + after });
        i += hit[0].length - 1;
      }
    }
    base += line.length + 1;
  }
  return out;
}

/** Apply source edits (non-overlapping), returning the new text. */
export function applyEdits(source: string, edits: readonly SourceEdit[]): string {
  let s = source;
  for (const e of [...edits].sort((a, b) => b.from - a.from || b.to - a.to)) s = s.slice(0, e.from) + e.text + s.slice(e.to);
  return s;
}

/** A line fix → a source edit. */
export function fixToEdit(source: string, fix: TextFix): SourceEdit {
  const lines = source.split('\n');
  if (fix.line > lines.length) return { from: source.length, to: source.length, text: fix.text };
  let pos = 0;
  for (let i = 0; i < fix.line - 1; i++) pos += lines[i].length + 1;
  const len = lines[fix.line - 1].length;
  const from = pos + Math.min(len, Math.max(0, fix.col - 1));
  const to = pos + Math.min(len, Math.max(0, fix.end - 1));
  return { from, to: Math.max(from, to), text: fix.text };
}

/** Apply several suggested fixes at once («Исправить всё»); overlapping ones keep the first. */
export function applyFixes(source: string, fixes: readonly TextFix[]): string {
  const edits: SourceEdit[] = [];
  for (const f of fixes) {
    const e = fixToEdit(source, f);
    if (edits.some((x) => e.from < x.to && x.from < e.to)) continue;
    edits.push(e);
  }
  return applyEdits(source, edits);
}

/** Edit distance with swaps of neighbours (Damerau–Levenshtein), capped (returns cap + 1 when larger). */
export function distance(a: string, b: string, cap = 3): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i);
  let prevPrev = prev;
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    let rowMin = i;
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
      // two swapped neighbours count as one typo
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1]) cur[j] = Math.min(cur[j], prevPrev[j - 2] + 1);
      rowMin = Math.min(rowMin, cur[j]);
    }
    if (rowMin > cap) return cap + 1;
    prevPrev = prev;
    prev = cur;
  }
  return prev[b.length];
}

/**
 * The known word a typo most likely meant: one edit for short words, two for longer ones, and
 * only when exactly one candidate is that close (an ambiguous typo gets no suggestion).
 */
export function closestWord(word: string, candidates: Iterable<string>): string | null {
  const w = word.toLowerCase().replace(/ё/g, 'е');
  if (w.length < 3) return null;
  const limit = w.length <= 5 ? 1 : 2;
  let best: string | null = null;
  let bestD = limit + 1;
  let tie = false;
  const seen = new Set<string>();
  for (const c of candidates) {
    const norm = c.toLowerCase().replace(/ё/g, 'е');
    // synonyms that differ only by ё/е are one candidate
    if (seen.has(norm)) continue;
    seen.add(norm);
    const d = distance(w, norm, limit);
    if (d === 0) return c;
    if (d < bestD) {
      best = c;
      bestD = d;
      tie = false;
    } else if (d === bestD) tie = true;
  }
  return best && !tie ? best : null;
}
